import React, { useState, useEffect } from "react";
import { addHours, isAfter } from "date-fns";
import moment from "moment";

import Dialog, { DialogContent, DialogActions } from "components/common/Dialog";
import CustomButton from "components/common/Button";
import TimeRangeSelector from "components/CheckInCheckOut/TimeRangeSelector";

import warningIcon from "assets/images/warning_amber.svg";

import "components/CheckInCheckOut/MissedClockOutModal/MissedClockOutModal.scss";

function MissedClockOutModal({ open, onClose, onSave, clockInTime, shiftDate, shiftEndTime }) {
  const [clockOutTime, setClockOutTime] = useState(null);
  const [timeError, setTimeError] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  const getMomentFromProp = (prop) => {
    if (!prop) return null;
    if (prop.year && typeof prop.year === "function") return prop;
    if (typeof prop === "number") return moment(prop);
    if (prop instanceof Date) {
      return moment({
        year: prop.getFullYear(),
        month: prop.getMonth(),
        date: prop.getDate(),
        hour: prop.getHours(),
        minute: prop.getMinutes(),
        second: 0,
        millisecond: 0,
      });
    }
    return moment.parseZone(prop);
  };

  const getShiftMoment = () => {
    return getMomentFromProp(shiftDate) || moment();
  };

  const getClockInMoment = () => {
    return getMomentFromProp(clockInTime);
  };

  useEffect(() => {
    if (open && shiftEndTime && !isInitialized) {
      const shiftEndMoment = getMomentFromProp(shiftEndTime);
      let shiftEndDate = null;

      if (shiftEndMoment && shiftEndMoment.isValid()) {
        shiftEndDate = new Date(
          shiftEndMoment.year(),
          shiftEndMoment.month(),
          shiftEndMoment.date(),
          shiftEndMoment.hour(),
          shiftEndMoment.minute(),
          0,
          0
        );
      }

      setClockOutTime(shiftEndDate);
      setTimeError("");
      setIsInitialized(true);
    } else if (!open) {
      // Reset initialization flag when modal closes
      setIsInitialized(false);
    }
  }, [open, shiftEndTime, isInitialized]);

  const getCombinedClockOutDateTime = () => {
    if (!clockOutTime) return null;

    const shiftMoment = getShiftMoment();
    const clockInMoment = getClockInMoment();

    if (!shiftMoment || !shiftMoment.isValid()) return null;

    const clockInHours = clockInMoment?.hour() || 0;
    const clockInMinutes = clockInMoment?.minute() || 0;
    const clockOutHours = clockOutTime.getHours();
    const clockOutMinutes = clockOutTime.getMinutes();

    const clockInTotalMinutes = clockInHours * 60 + clockInMinutes;
    const clockOutTotalMinutes = clockOutHours * 60 + clockOutMinutes;

    const resultMoment = shiftMoment.clone();

    if (clockOutTotalMinutes < clockInTotalMinutes) {
      resultMoment.add(1, "day");
    }

    resultMoment.set({
      hour: clockOutHours,
      minute: clockOutMinutes,
      second: 0,
      millisecond: 0,
    });

    if (!resultMoment.isValid()) return null;

    return new Date(
      resultMoment.year(),
      resultMoment.month(),
      resultMoment.date(),
      resultMoment.hour(),
      resultMoment.minute(),
      0,
      0
    );
  };

  const getTotalDuration = () => {
    const clockInMoment = getClockInMoment();
    const combinedDateTime = getCombinedClockOutDateTime();

    if (!clockOutTime || !clockInMoment || !combinedDateTime) return "0 hr";

    const clockInDate = new Date(
      clockInMoment.year(),
      clockInMoment.month(),
      clockInMoment.date(),
      clockInMoment.hour(),
      clockInMoment.minute(),
      0,
      0
    );

    const totalHoursCalc = Math.abs(combinedDateTime.getTime() - clockInDate.getTime()) / 36e5;

    const wholeHours = Math.floor(totalHoursCalc);
    const minutes = Math.round((totalHoursCalc - wholeHours) * 60);

    if (minutes === 0) {
      return `${wholeHours} hr`;
    }
    return `${wholeHours} hr ${minutes} min`;
  };

  const validateClockOutTime = (time) => {
    if (!time) {
      setTimeError("");
      return true;
    }

    if (!shiftEndTime) {
      setTimeError("");
      return true;
    }

    const maxAllowedTime = addHours(shiftEndTime, 3);

    if (isAfter(time, maxAllowedTime)) {
      setTimeError("Clock-out time cannot exceed 3 hours beyond the shift end time.");
      return false;
    }

    setTimeError("");
    return true;
  };

  const handleClockOutTimeChange = (time) => {
    setClockOutTime(time);
    validateClockOutTime(time);
  };

  const handleSave = () => {
    const combinedDateTime = getCombinedClockOutDateTime();

    if (combinedDateTime && !timeError) {
      onSave(combinedDateTime);
    }
  };

  const isFormValid = clockOutTime && !timeError;

  const dialogTitle = (
    <div className="missed-clock-out-modal__title-section">
      <div className="missed-clock-out-modal__icon mb-075">
        <img src={warningIcon} alt="Warning" />
      </div>
      <div className="missed-clock-out-modal__title-content">
        <p className="heading-h2-semibold">You missed your clock-out.</p>
      </div>
      <p className="para-body-m-regular color-text-body-primary">Add an end time to complete your previous shift.</p>
    </div>
  );

  const getDisplayDate = () => {
    const shiftMoment = getShiftMoment();
    const clockInMoment = getClockInMoment();

    if (!shiftMoment || !shiftMoment.isValid()) return "";

    const clockInHours = clockInMoment?.hour() || 0;
    const clockInMinutes = clockInMoment?.minute() || 0;
    const clockOutHours = clockOutTime?.getHours() || 0;
    const clockOutMinutes = clockOutTime?.getMinutes() || 0;

    const clockInTotalMinutes = clockInHours * 60 + clockInMinutes;
    const clockOutTotalMinutes = clockOutHours * 60 + clockOutMinutes;

    const displayMoment = shiftMoment.clone();
    if (clockOutTotalMinutes < clockInTotalMinutes) {
      displayMoment.add(1, "day");
    }

    return displayMoment.format("MMM D, YYYY");
  };

  const getHeading = () => {
    const shiftMoment = getShiftMoment();
    return shiftMoment && shiftMoment.isValid() ? shiftMoment.format("dddd, MMM D, YYYY") : "";
  };

  const getClockInTimeAsDate = () => {
    const clockInMoment = getClockInMoment();
    if (!clockInMoment || !clockInMoment.isValid()) return null;

    return new Date(
      clockInMoment.year(),
      clockInMoment.month(),
      clockInMoment.date(),
      clockInMoment.hour(),
      clockInMoment.minute(),
      0,
      0
    );
  };

  const dialogContent = (
    <DialogContent>
      <TimeRangeSelector
        showHeading
        heading={getHeading()}
        totalDuration={getTotalDuration() || "0 hr"}
        startTime={getClockInTimeAsDate()}
        endTime={clockOutTime}
        onStartTimeChange={() => {}}
        onEndTimeChange={handleClockOutTimeChange}
        startLabel="Clock in"
        endLabel="Clock out"
        startPlaceholder="hh:mm aa"
        endPlaceholder="12:20 AM"
        helperText=""
        showArrow
        startTimeDisabled
        endTimeDisabled={false}
        endTimeError={timeError}
        showNextDayLabel
        nextDayLabel="Date"
        nextDayText={getDisplayDate()}
      />
    </DialogContent>
  );

  const dialogActions = (
    <DialogActions
      action1={<CustomButton size="large" label="Cancel" onClick={onClose} variant="text" color="primary" />}
      action2={
        <CustomButton
          size="large"
          label="Save and close shift"
          onClick={handleSave}
          variant="contained"
          color="error"
          isDisabled={!isFormValid}
        />
      }
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      fullWidth
      title={dialogTitle}
      content={dialogContent}
      actions={dialogActions}
    />
  );
}

export default MissedClockOutModal;
